// deployment_class: customer_env_allowed
/**
 * The Analyzer view (V4_1b Theme 8 v1 · bug_UI_1k) — pick 2–4 projects, see them
 * side by side, and get Kathedral's grounded risk read. Rules-based + cited;
 * the model-backed probabilities are v2 (see the Analyzer spec). Lives as a mode
 * of the Projects overview, alongside Grid / Free.
 */
import { useState } from 'react';
import { type Deal } from './project-data';
import { useDeals } from './dashboard-source';
import { assessRisk, generateRead, parseDscr, DSCR_FLOOR } from './analyzer';
import { RichText } from '../shared/ui/RichText';

interface Metric { label: string; get: (d: Deal) => string; tone?: (d: Deal) => 'g' | 'y' | 'r' | ''; }
const METRICS: Metric[] = [
  { label: 'Lifecycle phase', get: (d) => `${d.phaseIdx + 1} / 9` },
  { label: 'Progress', get: (d) => `${d.progress}%` },
  { label: 'Budget (TDC)', get: (d) => d.budget },
  { label: 'DSCR', get: (d) => d.dscr, tone: (d) => (parseDscr(d.dscr) < DSCR_FLOOR ? 'r' : parseDscr(d.dscr) < 1.25 ? 'y' : 'g') },
  { label: 'Units', get: (d) => String(d.units) },
  { label: 'Status', get: (d) => d.status, tone: (d) => d.rag[0] },
  { label: 'Partner', get: (d) => d.partner.split(' · ')[0] },
];

export function Analyzer({ onOpen }: { onOpen: (k: string) => void }): JSX.Element {
  const { deals: DEALS, order: DEAL_ORDER } = useDeals();
  const [sel, setSel] = useState<string[]>([]);
  const toggle = (k: string): void =>
    setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : s.length >= 4 ? s : [...s, k]));
  const chosen = sel.map((k) => DEALS[k]);

  return (
    <div className="kd-anz">
      <div className="kd-anz-lead">Select 2–4 projects to compare and identify risk.</div>
      <div className="kd-anz-pick" role="group" aria-label="Choose projects to compare">
        {DEAL_ORDER.map((k) => {
          const on = sel.includes(k);
          return (
            <button key={k} className={`kd-anz-chip ${on ? 'on' : ''}`} aria-pressed={on}
              onClick={() => toggle(k)} disabled={!on && sel.length >= 4}>
              {DEALS[k].name}
            </button>
          );
        })}
      </div>

      {sel.length >= 2 ? (
        <>
          <table className="kd-anz-table">
            <thead>
              <tr>
                <th aria-hidden="true" />
                {sel.map((k) => (
                  <th key={k} scope="col">
                    <button className="kd-anz-name" onClick={() => onOpen(k)} aria-label={`Open ${DEALS[k].name}`}>
                      {DEALS[k].name} <span aria-hidden="true">→</span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRICS.map((mt) => (
                <tr key={mt.label}>
                  <th scope="row">{mt.label}</th>
                  {chosen.map((d) => {
                    const tone = mt.tone?.(d) ?? '';
                    return <td key={d.key} className={tone ? `t-${tone}` : ''}>{mt.get(d)}</td>;
                  })}
                </tr>
              ))}
              <tr className="kd-anz-riskrow">
                <th scope="row">Risk score</th>
                {chosen.map((d) => {
                  const r = assessRisk(d);
                  return (
                    <td key={d.key}>
                      <span className={`kd-anz-risk t-${r.tone}`}>{r.score}/100 · {r.level}</span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>

          <div className="kd-anz-read">
            <div className="kd-anz-readh"><span className="kd-kk">K</span> Kathedral’s read</div>
            <div className="kd-anz-readb"><RichText value={generateRead(sel, DEALS)} /></div>
          </div>
        </>
      ) : (
        <div className="kd-anz-empty">Pick at least two projects above to see the side-by-side and Kathedral’s risk read.</div>
      )}
    </div>
  );
}
